import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import { Input } from '#/modules/commonComponents/form/Input';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import ManufacturingMaterialSelectDialog from '#/modules/crmMgmt/customerOrder/components/ManufacturingMaterialSelectDialog';
import {
  applyDraftPlanGroupSelections,
  applyManufacturingMaterialSelection,
  formatManufacturingMaterialOptionalQty,
  isManufacturingMaterialSelectionComplete,
  rowNeedsManufacturingMaterialSelection,
} from '#/modules/crmMgmt/customerOrder/helper/manufacturingMaterialSubstitute';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { formatDisplayQty } from '#/utils/formatNumber';
import { toast } from '#/utils/toast';

/** 選取｜物料｜選料｜已選｜總需求｜缺口｜製作量｜製作中 */
/* eslint-disable max-len -- Tailwind arbitrary grid-cols must stay as one static class string */
const LINE_GRID_COLS =
  'grid-cols-[minmax(2.5rem,2.5rem)_minmax(8rem,1.1fr)_minmax(4.5rem,4.5rem)_minmax(8rem,1.1fr)_minmax(3.25rem,4rem)_minmax(3.25rem,4rem)_minmax(6rem,7.5rem)_minmax(3.25rem,4rem)]';
/* eslint-enable max-len */

const LINE_MIN_WIDTH = 'min-w-[52rem]';

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-[var(--z-content-sticky)] grid w-full gap-2.5',
  'border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full items-center gap-2.5 px-3 py-2',
  LINE_GRID_COLS,
);

/**
 * @returns {{
 *   kind: 'plain'|'status',
 *   text: string,
 *   complete?: boolean,
 * }}
 */
function resolveSelectedMaterialDisplay(row, canSelect) {
  if (!canSelect) {
    return { kind: 'plain', text: '無' };
  }
  if (isManufacturingMaterialSelectionComplete(row)) {
    return {
      kind: 'status',
      complete: true,
      text: '已完成替代料選擇',
    };
  }
  return {
    kind: 'status',
    complete: false,
    text: '尚未完成替代料選擇',
  };
}

/**
 * 建立多階層工單 — 物料計劃操作列（thead＋列共用 grid；禁止 ListTablePanel）。
 */
const ManufacturingMaterialsList = ({
  rows = [],
  onRowsChange,
}) => {
  const [keyword, setKeyword] = useState('');
  const [selectSource, setSelectSource] = useState(null);

  const filteredRows = useMemo(() => {
    const q = String(keyword ?? '').trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = [
        row?.mtrNo,
        row?.mtrName,
        row?.selectedMtrNo,
        row?.selectedMtrName,
        row?.productLabel,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, keyword]);

  const allSelected = rows.length > 0 && rows.every((row) => row.selected);

  function handleToggleAll(checked) {
    onRowsChange?.(rows.map((row) => ({ ...row, selected: checked })));
  }

  function handleToggleRow(rowId, checked) {
    onRowsChange?.(
      rows.map((item) => (
        item.id === rowId
          ? { ...item, selected: checked }
          : item
      )),
    );
  }

  function handleManufacturingQtyChange(rowId, nextValue) {
    onRowsChange?.(
      rows.map((item) => (
        item.id === rowId
          ? { ...item, manufacturingQty: nextValue }
          : item
      )),
    );
  }

  function handleSelectConfirm(payload) {
    if (payload?.mode === 'group') {
      const result = applyDraftPlanGroupSelections(rows, {
        rowId: payload.rowId,
        selections: payload.selections,
      });
      if (!result.ok) {
        toast.warning(result.message);
        return;
      }
      onRowsChange?.(result.rows);
      return;
    }

    const result = applyManufacturingMaterialSelection(rows, {
      rowId: payload.rowId,
      material: payload.material,
    });
    if (!result.ok) {
      toast.warning(result.message);
      return;
    }
    onRowsChange?.(result.rows);
  }

  return (
    <>
      <div className="sticky top-0 z-10 flex min-w-0 items-center gap-1.5 bg-panel">
        <TableSearchInput
          useKeyword
          keyword={keyword}
          onKeywordChange={setKeyword}
          placeholder="搜尋料號、名稱"
          className="min-w-0 w-full max-w-md flex-1 text-foreground"
        />
      </div>

      {filteredRows.length === 0 ? (
        <p className="form-section__empty-state">尚無物料計劃列</p>
      ) : (
        <div
          className={cn(
            'overflow-x-auto rounded-md border border-gray-200',
            'max-h-[min(32rem,calc(100vh-14rem))] overflow-y-auto',
          )}
        >
          <div className={cn('w-full', LINE_MIN_WIDTH)}>
            <div className={LINE_HEADER_CLASS}>
              <span className="inline-flex justify-center">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) => handleToggleAll(Boolean(checked))}
                  aria-label="全選"
                />
              </span>
              <span>物料</span>
              <span className="text-right">選料</span>
              <span>替代料</span>
              <span className="text-right">總需求</span>
              <span className="text-right">缺口</span>
              <span className="text-right">製作量</span>
              <span className="text-right">製作中</span>
            </div>

            <ul className="m-0 list-none divide-y divide-gray-200 p-0">
              {filteredRows.map((row) => {
                const canSelect = rowNeedsManufacturingMaterialSelection(row);
                const qtyBelowShortage = Number(row.manufacturingQty)
                  < Number(row.shortageQty ?? row.totalRequiredQty);
                const selectedDisplay = resolveSelectedMaterialDisplay(row, canSelect);

                return (
                  <li key={row.id} className={LINE_ROW_CLASS}>
                    <div className="inline-flex justify-center">
                      <Checkbox
                        checked={Boolean(row.selected)}
                        onCheckedChange={(checked) => {
                          handleToggleRow(row.id, Boolean(checked));
                        }}
                        aria-label={`選取 ${row.mtrNo || '物料'}`}
                      />
                    </div>

                    <div className="min-w-0">
                      <MaterialNameStack
                        name={row.mtrName}
                        code={row.mtrNo || row.mtrId}
                      />
                      {row.productLabel ? (
                        <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                          {row.productLabel}
                        </p>
                      ) : null}
                    </div>

                    <div
                      className={cn(
                        'inline-flex w-full min-w-[4.5rem] shrink-0',
                        'items-center justify-end',
                      )}
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!canSelect}
                        onClick={() => setSelectSource(row)}
                      >
                        選料
                      </Button>
                    </div>

                    <div className="min-w-0">
                      {selectedDisplay.kind === 'status' ? (
                        <span
                          className={cn(
                            'text-xs font-medium',
                            selectedDisplay.complete
                              ? 'text-green-700'
                              : 'text-red-600',
                          )}
                        >
                          {selectedDisplay.text}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {selectedDisplay.text}
                        </span>
                      )}
                    </div>

                    <p className="text-right text-sm tabular-nums text-gray-800">
                      {formatDisplayQty(row.totalRequiredQty)}
                    </p>

                    <p className="text-right text-sm tabular-nums text-gray-800">
                      {formatDisplayQty(row.shortageQty ?? row.totalRequiredQty)}
                    </p>

                    <div className="min-w-0">
                      <Input
                        type="number"
                        min={0}
                        className="h-8 w-full"
                        value={row.manufacturingQty ?? ''}
                        onChange={(event) => {
                          handleManufacturingQtyChange(row.id, event.target.value);
                        }}
                        aria-label={`${row.mtrNo || '物料'} 製作量`}
                      />
                      {qtyBelowShortage ? (
                        <p className="text-[10px] leading-none text-amber-700">
                          ＜缺口
                        </p>
                      ) : null}
                    </div>

                    <p className="text-right text-sm tabular-nums text-gray-800">
                      {formatManufacturingMaterialOptionalQty(row.inProductionQty)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      <ManufacturingMaterialSelectDialog
        open={Boolean(selectSource)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSelectSource(null);
          }
        }}
        sourceRow={selectSource}
        onConfirm={handleSelectConfirm}
      />
    </>
  );
};

ManufacturingMaterialsList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  onRowsChange: PropTypes.func,
};

export default ManufacturingMaterialsList;
